import { trpc, RouterOutputs } from '@org/clients/school';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DatePicker,
  Input,
  Label,
  Skeleton,
  Switch,
  unwrapTrpcData,
} from '@org/ui';
import { Link } from '@tanstack/react-router';
import { endOfDay, format } from 'date-fns';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { IdentityStatusChip } from '../identity/identity-review-list';
import { toDate } from '../identity/identity-review-helpers';
import { getMutationErrorMessage } from '../shared/multi-step-utils';

type BaseForUser = RouterOutputs['access']['baseForUser'];
type BasePurchase = BaseForUser['purchases'][number];

type BaseAccessSectionProps = {
  userId: string;
};

type WindowEditor = {
  purchaseId: string;
  endsAt: Date | undefined;
  never: boolean;
};

/**
 * "Base access" block at the top of the student Access dialog: for
 * self-learning students, their identity validation, trial / subscription
 * state and the base purchases (grant / change end date); for everyone else a
 * single line saying it is included.
 */
export function BaseAccessSection({ userId }: BaseAccessSectionProps) {
  const { t } = useTranslation('admin');
  const utils = trpc.useUtils();

  const { data, isLoading } = trpc.access.baseForUser.useQuery({ userId }, { enabled: !!userId });
  const info = unwrapTrpcData<BaseForUser>(data as BaseForUser) ?? null;

  const [grantNever, setGrantNever] = useState(false);
  const [grantEndsAt, setGrantEndsAt] = useState<Date | undefined>(undefined);
  const [grantNote, setGrantNote] = useState('');
  const [editor, setEditor] = useState<WindowEditor | null>(null);

  const invalidate = async () => {
    await utils.access.baseForUser.invalidate({ userId });
    await utils.journeyAccess.listForUser.invalidate({ userId });
    await utils.journeys.getUserJourneys.invalidate({ userId, activeOnly: false });
    await utils.progress.getActiveJourney.invalidate({ userId });
    await utils.users.listStudentsWithDetails.invalidate();
  };

  const grantMutation = trpc.access.grantBase.useMutation({
    onSuccess: async () => {
      toast.success(t('baseAccess.toasts.grantSuccess'));
      setGrantNever(false);
      setGrantEndsAt(undefined);
      setGrantNote('');
      await invalidate();
    },
    onError: (error: unknown) =>
      toast.error(t('baseAccess.toasts.grantError', { message: getMutationErrorMessage(error) })),
  });

  const updateMutation = trpc.access.updateBaseWindow.useMutation({
    onSuccess: async () => {
      toast.success(t('baseAccess.toasts.updateSuccess'));
      setEditor(null);
      await invalidate();
    },
    onError: (error: unknown) =>
      toast.error(t('baseAccess.toasts.updateError', { message: getMutationErrorMessage(error) })),
  });

  const formatDate = (value: unknown) => {
    const date = toDate(value);
    return date ? format(date, 'PP') : '';
  };

  const handleGrant = () => {
    if (!grantNever && !grantEndsAt) {
      toast.error(t('journeyAccess.dialog.toasts.pickDate'));
      return;
    }
    grantMutation.mutate({
      userId,
      accessEndsAt: grantNever ? null : endOfDay(grantEndsAt as Date),
      note: grantNote.trim() ? grantNote.trim() : undefined,
    });
  };

  const handleSaveWindow = () => {
    if (!editor) return;
    if (!editor.never && !editor.endsAt) {
      toast.error(t('journeyAccess.dialog.toasts.pickDate'));
      return;
    }
    updateMutation.mutate({
      purchaseId: editor.purchaseId,
      accessEndsAt: editor.never ? null : endOfDay(editor.endsAt as Date),
    });
  };

  const stateLine = () => {
    if (!info) return '';
    const { base } = info;
    const date = base.endsAt ? formatDate(base.endsAt) : '';
    switch (base.state) {
      case 'trial':
        return t('baseAccess.state.trial', { date });
      case 'active':
        return base.endsAt ? t('baseAccess.state.activeUntil', { date }) : t('baseAccess.state.activeForever');
      case 'expired':
        return t('baseAccess.state.expired', { date });
      default:
        return t('baseAccess.state.none');
    }
  };

  const sourceLabel = (source: BasePurchase['source']) =>
    t(`baseAccess.source.${source}`, { defaultValue: String(source ?? '') });

  const statusLabel = (status: BasePurchase['status']) =>
    t(`baseAccess.purchaseStatus.${status}`, { defaultValue: String(status ?? '') });

  if (isLoading) {
    return (
      <div className="mt-2 space-y-2">
        <Skeleton className="h-6 w-1/2" />
        <Skeleton className="h-12 w-full" />
      </div>
    );
  }

  if (!info) return null;

  if (!info.base.required) {
    return (
      <p className="mt-2 rounded-md border border-border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
        {t('baseAccess.included')}
      </p>
    );
  }

  const identityPending = info.identity.status === 'needs_review' || info.identity.status === 'declined';

  return (
    <Card className="mt-2 shadow-sm">
      <CardHeader className="bg-muted/50 rounded-t-md border-b pb-3 pt-3">
        <CardTitle className="flex flex-wrap items-center gap-2 text-base font-medium">
          {t('baseAccess.title')}
          {info.base.product ? (
            <span className="text-sm font-normal text-muted-foreground">· {info.base.product.name}</span>
          ) : null}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-4">
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <span className="text-muted-foreground">{t('baseAccess.identityLabel')}</span>
          <IdentityStatusChip status={info.identity.status} />
          {identityPending && (
            <Link to="/identity-reviews" className="text-xs underline text-rose-700">
              {t('baseAccess.reviewLink')}
            </Link>
          )}
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
          <span className="text-muted-foreground">{t('baseAccess.stateLabel')}</span>
          <Badge
            variant={info.base.state === 'active' || info.base.state === 'trial' ? 'outline' : 'destructive'}
            className={
              info.base.state === 'trial'
                ? 'border-amber-400 bg-amber-50 text-amber-900'
                : info.base.state === 'active'
                  ? 'border-emerald-300 bg-emerald-50 text-emerald-800'
                  : undefined
            }
          >
            {stateLine()}
          </Badge>
          {typeof info.base.daysLeft === 'number' && info.base.state !== 'expired' && (
            <span className="text-xs text-muted-foreground">
              {t('baseAccess.daysLeft', { count: info.base.daysLeft })}
            </span>
          )}
        </div>
        {!info.base.product && (
          <p className="mt-2 text-xs text-amber-800">{t('baseAccess.noProduct')}</p>
        )}

        {info.purchases.length > 0 && (
          <ul className="mt-4 flex flex-col gap-2">
            {info.purchases.map((purchase) => {
              const current = editor && editor.purchaseId === purchase.purchaseId ? editor : null;
              return (
                <li
                  key={purchase.purchaseId}
                  className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border px-3 py-2 text-sm"
                >
                  <div className="flex flex-col gap-1">
                    <span className="flex flex-wrap items-center gap-2">
                      <Badge variant="outline">{sourceLabel(purchase.source)}</Badge>
                      <span>{statusLabel(purchase.status)}</span>
                      {purchase.asaasStatus ? (
                        <span className="text-xs text-muted-foreground">ASAAS · {purchase.asaasStatus}</span>
                      ) : null}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {t('baseAccess.purchasedAt', { date: formatDate(purchase.purchasedAt) })}
                      {' · '}
                      {purchase.accessEndsAt
                        ? t('baseAccess.endsAt', { date: formatDate(purchase.accessEndsAt) })
                        : t('journeyAccess.dialog.never')}
                    </span>
                  </div>
                  {current ? (
                    <div className="flex min-w-[220px] flex-col gap-2">
                      <DatePicker
                        value={current.endsAt}
                        onChange={(date) => setEditor({ ...current, endsAt: date })}
                        placeholder={t('journeyAccess.dialog.actions.pickEndDate')}
                        disabled={current.never}
                      />
                      <label className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Switch
                          checked={current.never}
                          onCheckedChange={(checked) => setEditor({ ...current, never: checked })}
                        />
                        {t('journeyAccess.dialog.neverExpires')}
                      </label>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={handleSaveWindow}
                          isLoading={updateMutation.isPending}
                          loadingText={t('common.status.saving')}
                        >
                          {t('journeyAccess.dialog.actions.save')}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditor(null)} disabled={updateMutation.isPending}>
                          {t('journeyAccess.dialog.actions.cancel')}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    purchase.status === 'granted' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          setEditor({
                            purchaseId: purchase.purchaseId,
                            endsAt: toDate(purchase.accessEndsAt) ?? undefined,
                            never: !purchase.accessEndsAt,
                          })
                        }
                      >
                        {t('journeyAccess.dialog.actions.changeEnd')}
                      </Button>
                    )
                  )}
                </li>
              );
            })}
          </ul>
        )}

        <div className="mt-4 grid grid-cols-1 gap-4 border-t pt-4 md:grid-cols-2">
          <div className="flex flex-col gap-2">
            <Label>{t('baseAccess.grant.endDate')}</Label>
            <DatePicker
              value={grantEndsAt}
              onChange={setGrantEndsAt}
              placeholder={t('journeyAccess.dialog.actions.pickEndDate')}
              disabled={grantNever}
            />
            <label className="flex items-center gap-2 text-sm text-muted-foreground">
              <Switch checked={grantNever} onCheckedChange={setGrantNever} />
              {t('journeyAccess.dialog.neverExpires')}
            </label>
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="base-access-grant-note">{t('journeyAccess.dialog.grant.note')}</Label>
            <Input
              id="base-access-grant-note"
              value={grantNote}
              onChange={(event) => setGrantNote(event.target.value)}
              placeholder={t('journeyAccess.dialog.grant.notePlaceholder')}
            />
          </div>
        </div>
        <div className="mt-3 flex justify-end">
          <Button
            variant="outline"
            onClick={handleGrant}
            disabled={!info.base.product || (!grantNever && !grantEndsAt)}
            isLoading={grantMutation.isPending}
            loadingText={t('common.status.saving')}
          >
            {t('baseAccess.grant.submit')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
